import { AlertTypeEnum } from '@/models/Alert';
import { FunnelIcon } from '@heroicons/react/20/solid';
import { forwardRef, InputHTMLAttributes } from 'react';
import { Control, useWatch } from 'react-hook-form';
import Popup from '@/components/ui/custom/Popup';
import MainButton from '@/components/ui/custom/buttons/MainButton';
import CheckGroup, {
  CheckOption,
} from '@/components/ui/custom/dataInput/CheckGroup';
import NotificationTag, { getAlertTagsStyles } from './NotificationTag';
import { useTranslation } from 'react-i18next';
import { NotificationFilterFields } from './NotificationTypes';
import ConditionalRenderer from '@/components/ui/custom/ConditionalRenderer';
import { ClassNameComponent } from '@/utils/ClassNameComponent';

type NotificationTypeFilterProps = InputHTMLAttributes<HTMLInputElement> & {
  control?: Control<NotificationFilterFields>;
  className?: Pick<ClassNameComponent, 'base' | 'input'>;
};

const NotificationTypeFilter = forwardRef<
  HTMLInputElement,
  NotificationTypeFilterProps
>(({ control, ...props }, ref) => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'alerts.filters',
  });
  const { t: tBase } = useTranslation('translation');
  const options: CheckOption[] = Object.values(AlertTypeEnum).map(type => ({
    value: type,
    label: <NotificationTag type={type} />,
  }));
  const { types } = useWatch({
    control,
    exact: true,
  });
  return (
    <Popup
      placement="bottom-end"
      offset={[2, 2]}
      reference={
        <div className="flex gap-2 items-center">
          <MainButton
            color="custom"
            text={t('type').toLowerCase()}
            icon={<FunnelIcon className="w-4 h-4" />}
            className="flex-row-reverse gap-1"
          />
          <ConditionalRenderer condition={types?.length}>
            <ul className="flex gap-1.5">
              {types?.map(type => {
                const { icon: Icon, className } =
                  getAlertTagsStyles(tBase)[type];
                return (
                  <Icon className={`${className.text} w-4 h-4`} key={type} />
                );
              })}
            </ul>
          </ConditionalRenderer>
        </div>
      }
    >
      <div className="p-2.5 bg-base-100 rounded-md shadow-default border border-neutral-content">
        <CheckGroup options={options} {...props} ref={ref} />
      </div>
    </Popup>
  );
});

export default NotificationTypeFilter;
